import { Modal, IconButton, Button, Banner, NodeLogo } from '@hevoio/hevo-ui-toolkit';
import { IconX, IconInfoCircle, IconAlertTriangleFilled } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { AttentionItem } from '../../interfaces/attentionItems';
import styles from './AttentionItemsModal.module.scss';

const BASE_STYLING = {
  padding: 0,
  borderRadius: '20px',
  overflow: 'hidden',
  backgroundColor: 'var(--surface-bg-secondary-color)',
  border: '1px solid var(--zora-bg-soft-200)'
} as const;
const TWO_PANE_STYLING = { ...BASE_STYLING, maxWidth: '820px', width: '820px' } as const;
const SINGLE_PANE_STYLING = { ...BASE_STYLING, maxWidth: '560px', width: '560px' } as const;

interface AttentionItemsModalProps {
  open: boolean;
  items: AttentionItem[];
  /** Close (X) — dismisses the whole modal without recording per-item dismissals. */
  onClose: () => void;
  onRemindLater: (id: string) => void;
  onCta: (item: AttentionItem) => void;
}

function getHeaderText(count: number) {
  return count === 1
    ? 'There is 1 item that needs your attention'
    : `There are ${count} items that need your attention`;
}

function SeverityIcon({ severity }: { severity: AttentionItem['severity'] }) {
  return severity === 'warning' ? (
    <IconAlertTriangleFilled size={20} className={styles.warningIcon} />
  ) : (
    <IconInfoCircle size={20} className={styles.infoIcon} />
  );
}

export function AttentionItemsModal({
  open,
  items,
  onClose,
  onRemindLater,
  onCta
}: AttentionItemsModalProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Lock the pane layout to the largest item count seen this session so dismissing
  // an item (which shrinks `items`) doesn't resize the modal / drop the sidebar mid-flow.
  const [paneCount, setPaneCount] = useState(() => items.length);
  useEffect(() => {
    if (items.length > paneCount) setPaneCount(items.length);
  }, [items.length, paneCount]);

  // Keep selection in range as items get dismissed/removed.
  useEffect(() => {
    setSelectedIndex(prev => Math.min(prev, Math.max(items.length - 1, 0)));
  }, [items.length]);

  if (!items.length) return null;

  const selected = items[selectedIndex] ?? items[0];
  const isFirst = selectedIndex === 0;
  const isLast = selectedIndex === items.length - 1;
  const hasSidebar = paneCount > 1;

  return (
    <Modal
      open={open}
      onClose={onClose}
      styling={hasSidebar ? TWO_PANE_STYLING : SINGLE_PANE_STYLING}
      dataId='attention-items-modal'
    >
      <div className={styles.modalWrapper}>
        {hasSidebar ? (
          <div className={styles.sidebar}>
            <p className={styles.sidebarTitle}>{getHeaderText(items.length)}</p>

            <ul className={styles.itemList}>
              {items.map((item, index) => (
                <li key={item.id}>
                  <button
                    type='button'
                    data-id={`attention-item-${item.id}`}
                    className={clsx(styles.itemRow, index === selectedIndex && styles.itemRowActive)}
                    onClick={() => setSelectedIndex(index)}
                  >
                    <SeverityIcon severity={item.severity} />
                    <span className={styles.itemTitle}>{item.listLabel ?? item.title}</span>
                  </button>
                </li>
              ))}
            </ul>

            <div className={styles.nav}>
              <Button
                variation='outline'
                size='sm'
                dataId='attention-items-back'
                disabled={isFirst}
                onClick={() => setSelectedIndex(index => Math.max(index - 1, 0))}
              >
                Back
              </Button>
              <Button
                variation='outline'
                size='sm'
                dataId='attention-items-next'
                disabled={isLast}
                onClick={() => setSelectedIndex(index => Math.min(index + 1, items.length - 1))}
              >
                Next
              </Button>
            </div>
          </div>
        ) : null}

        <div className={clsx(styles.detail, !hasSidebar && styles.detailFull)}>
          <div className={styles.closeButton}>
            <IconButton label='Close' dataId='attention-items-close' onClick={onClose}>
              <IconX size={20} />
            </IconButton>
          </div>

          <div className={styles.hero}>
            {selected.logoUrl ? (
              <div className={styles.logoWrapper}>
                <NodeLogo
                  size={6}
                  logoURL={selected.logoUrl}
                  darkModeLogoURL={selected.logoUrl}
                  dataId={`attention-item-logo-${selected.id}`}
                />
              </div>
            ) : null}
          </div>

          <div className={styles.content}>
            <p className={styles.title}>{selected.title}</p>
            <p className={styles.description}>{selected.body}</p>
            {selected.banner ? (
              <Banner
                open
                color={selected.banner.color}
                icon={selected.banner.icon}
                dataId={`attention-item-banner-${selected.id}`}
              >
                {selected.banner.text}
              </Banner>
            ) : null}
          </div>

          <div className={styles.actions}>
            <Button
              variation='outline'
              size='sm'
              dataId='attention-items-remind-later'
              className={styles.actionBtn}
              onClick={() => onRemindLater(selected.id)}
            >
              Remind me later
            </Button>
            <Button
              size='sm'
              dataId='attention-items-cta'
              className={styles.actionBtn}
              onClick={() => onCta(selected)}
            >
              {selected.primaryCta.label}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
